/**
 * VolkSign date/time display rules:
 * - API sends datetimes in UTC (e.g. "2026-05-15 18:16:04" with no offset).
 * - Display calendar dates as dd/mm/yyyy (UTC date components).
 * - Display clock times as 24h HH:mm with a "UTC" suffix.
 */

const DD_MM_YYYY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** API timestamps like `2026-05-15 18:16:04` — UTC on the server, no timezone suffix. */
const API_DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

const formatUtcDateToDDMMYYYY = (dt) => {
  const day = String(dt.getUTCDate()).padStart(2, '0');
  const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const year = dt.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const buildUtcDateFromApiMatch = (m) => {
  const sec = m[6] != null ? parseInt(m[6], 10) : 0;
  return new Date(Date.UTC(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
    parseInt(m[4], 10),
    parseInt(m[5], 10),
    sec
  ));
};

/**
 * Parse API date/time strings as UTC. Without this, `new Date("2026-05-15 18:16:04")` is
 * interpreted as local time, then getUTCHours() shifts the displayed UTC clock.
 */
export const parseApiDateTimeAsUtc = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const s = String(value).trim();
  if (!s) return null;
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const m = API_DATETIME_PATTERN.exec(s);
  if (m) {
    const dt = buildUtcDateFromApiMatch(m);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const isoDateOnly = ISO_DATE_PATTERN.exec(s);
  if (isoDateOnly) {
    const dt = new Date(Date.UTC(
      parseInt(isoDateOnly[1], 10),
      parseInt(isoDateOnly[2], 10) - 1,
      parseInt(isoDateOnly[3], 10)
    ));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

/** dd/mm/yyyy from an API UTC datetime (or Date). */
export const formatDateUTC = (value) => {
  if (!value) return 'N/A';
  const dt = parseApiDateTimeAsUtc(value);
  if (!dt) return 'N/A';
  return formatUtcDateToDDMMYYYY(dt);
};

/** True when `value` is a valid calendar date in DD/MM/YYYY (UTC components). */
export const isValidDateDDMMYYYY = (value) => {
  const s = String(value ?? '').trim();
  const m = DD_MM_YYYY_PATTERN.exec(s);
  if (!m) return false;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year
    && dt.getUTCMonth() === month - 1
    && dt.getUTCDate() === day
  );
};

/** Normalize API/legacy values to DD/MM/YYYY for display and signing submit. */
export const normalizeToDateDDMMYYYY = (value) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (isValidDateDDMMYYYY(s)) return s;

  const apiDt = API_DATETIME_PATTERN.exec(s);
  if (apiDt) {
    const dt = buildUtcDateFromApiMatch(apiDt);
    if (!Number.isNaN(dt.getTime())) {
      return formatUtcDateToDDMMYYYY(dt);
    }
  }

  const iso = ISO_DATE_PATTERN.exec(s);
  if (iso) {
    const year = parseInt(iso[1], 10);
    const month = parseInt(iso[2], 10);
    const day = parseInt(iso[3], 10);
    const dt = new Date(Date.UTC(year, month - 1, day));
    if (!Number.isNaN(dt.getTime())) {
      return formatUtcDateToDDMMYYYY(dt);
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatUtcDateToDDMMYYYY(value);
  }
  return s;
};

/** Normalize to YYYY-MM-DD for native `<input type="date">` value. */
export const normalizeToDateISO = (value) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const iso = ISO_DATE_PATTERN.exec(s);
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const apiDt = API_DATETIME_PATTERN.exec(s);
  if (apiDt) {
    return `${apiDt[1]}-${apiDt[2]}-${apiDt[3]}`;
  }
  const ddmm = normalizeToDateDDMMYYYY(s);
  if (!isValidDateDDMMYYYY(ddmm)) return '';
  const m = DD_MM_YYYY_PATTERN.exec(ddmm);
  return `${m[3]}-${m[2]}-${m[1]}`;
};

export const formatTimeUTC24 = (value) => {
  if (!value) return 'N/A';
  const dt = parseApiDateTimeAsUtc(value);
  if (!dt) return 'N/A';
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
};

/** dd/mm/yyyy HH:mm UTC — for envelope progress, signing completion, etc. */
export const formatDateTimeUTC = (value) => {
  if (!value) return 'N/A';
  const dt = parseApiDateTimeAsUtc(value);
  if (!dt) return 'N/A';
  return `${formatUtcDateToDDMMYYYY(dt)} ${formatTimeUTC24(dt)}`;
};

export const formatApiDateTime = formatDateTimeUTC;
